
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Send, AlertCircle } from "lucide-react";
import SubscriptionWebhookTester from "./SubscriptionWebhookTester";

interface WebhookFunction {
  name: string;
  description: string;
  type: "manual" | "automatic";
  testFunction: () => Promise<any>;
}

const WebhookTester = () => {
  const { toast } = useToast();
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  const setLoading = (functionName: string, loading: boolean) => {
    setLoadingStates(prev => ({ ...prev, [functionName]: loading }));
  };

  // Test functions for each webhook
  const testWelcomeEmailWebhook = async () => {
    try {
      const { data, error } = await supabase.rpc('test_welcome_email_trigger');
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Welcome email webhook error:', error);
      throw error;
    }
  };

  const testBookingWebhook = async () => {
    try {
      // Get a recent booking to test with
      const { data: bookings, error: bookingError } = await supabase
        .from('bookings')
        .select('id')
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (bookingError) throw bookingError;
      
      if (!bookings || bookings.length === 0) {
        throw new Error('No bookings found to test with. Create a booking first.');
      }

      console.log('Testing booking webhook with booking ID:', bookings[0].id);

      const { data, error } = await supabase.rpc('manual_send_booking_webhook', {
        booking_id: bookings[0].id
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Booking webhook error:', error);
      throw error;
    }
  };

  const testCheckinWebhook = async () => {
    try {
      // Get any booking to test with (not just confirmed ones for testing)
      const { data: bookings, error: bookingError } = await supabase
        .from('bookings')
        .select('id, status')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (bookingError) throw bookingError;
      
      if (!bookings || bookings.length === 0) {
        throw new Error('No bookings found to test with. Create a booking first.');
      }

      // Try to find a confirmed booking first, otherwise use any booking
      const testBooking = bookings.find(b => b.status === 'confirmed') || bookings[0];
      
      console.log('Testing checkin webhook with booking ID:', testBooking.id, 'Status:', testBooking.status);

      const { data, error } = await supabase.rpc('manual_send_checkin_webhook', {
        booking_id: testBooking.id
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Checkin webhook error:', error);
      throw error;
    }
  };

  const testScheduledCheckinWebhooks = async () => {
    try {
      console.log('Testing scheduled checkin webhooks function...');
      
      const { data, error } = await supabase.rpc('send_checkin_available_webhooks');
      if (error) throw error;
      
      return { 
        success: true, 
        message: 'Scheduled check-in webhook function executed successfully',
        executed_at: new Date().toISOString()
      };
    } catch (error) {
      console.error('Scheduled checkin webhook error:', error);
      throw error;
    }
  };

  const testSubscriptionSessionsReset = async () => {
    try {
      console.log('Testing subscription sessions reset...');
      
      // Find a sessions_per_month subscription to test with
      const { data: subscriptions, error: subError } = await supabase
        .from('subscriptions')
        .select(`
          id,
          user_id,
          membership_id,
          memberships!inner(
            membership_type,
            session_count
          )
        `)
        .eq('memberships.membership_type', 'sessions_per_month')
        .eq('status', 'active')
        .limit(1);
      
      if (subError) throw subError;
      
      if (!subscriptions || subscriptions.length === 0) {
        throw new Error('No active sessions_per_month subscriptions found. Create one first.');
      }

      const subscription = subscriptions[0];
      
      // Reset the sessions for testing
      const { error: resetError } = await supabase
        .from('user_memberships')
        .update({
          sessions_remaining: subscription.memberships.session_count || 0,
          sessions_used: 0,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', subscription.user_id)
        .eq('membership_id', subscription.membership_id);

      if (resetError) throw resetError;

      return {
        success: true,
        message: 'Sessions reset successfully for sessions_per_month membership',
        subscription_id: subscription.id,
        sessions_reset_to: subscription.memberships.session_count
      };
    } catch (error) {
      console.error('Subscription sessions reset error:', error);
      throw error;
    }
  };

  const webhookFunctions: WebhookFunction[] = [
    {
      name: "Welcome Email Webhook",
      description: "Tests the welcome email trigger and Pabbly signup webhook",
      type: "automatic",
      testFunction: testWelcomeEmailWebhook
    },
    {
      name: "New Booking Webhook", 
      description: "Tests the new booking confirmation webhook sent to Pabbly",
      type: "automatic",
      testFunction: testBookingWebhook
    },
    {
      name: "Check-in Available Webhook",
      description: "Tests the check-in notification webhook (5 minutes before booking)",
      type: "automatic", 
      testFunction: testCheckinWebhook
    },
    {
      name: "Scheduled Check-in Webhooks",
      description: "Tests the automated cron job that sends check-in webhooks",
      type: "automatic",
      testFunction: testScheduledCheckinWebhooks
    },
    {
      name: "Sessions Per Month Reset",
      description: "Tests the monthly sessions reset for sessions_per_month memberships",
      type: "manual",
      testFunction: testSubscriptionSessionsReset
    },
    {
      name: "Checkout Webhook",
      description: "Tests the checkout completion webhook with customer review data",
      type: "manual",
      testFunction: async () => {
        // Find a completed booking to test with
        const { data: bookings, error: bookingError } = await supabase
          .from('bookings')
          .select('id, booking_number, status')
          .eq('status', 'completed')
          .order('updated_at', { ascending: false })
          .limit(1);
        
        if (bookingError) throw bookingError;
        
        if (!bookings || bookings.length === 0) {
          throw new Error('No completed bookings found to test with. Complete a booking first.');
        }

        console.log('Testing checkout webhook with booking ID:', bookings[0].id);

        const { data, error } = await supabase.rpc('manual_send_checkout_webhook', {
          booking_id: bookings[0].id
        });
        
        if (error) throw error;
        return data;
      }
    },
    {
      name: "Error Logging System",
      description: "Tests the enhanced error logging system with webhook notifications",
      type: "manual",
      testFunction: async () => {
        console.log('Testing error logging system...');
        
        // Test logging an error
        const { data, error } = await supabase.rpc('log_error', {
          p_error_type: 'test_error',
          p_error_message: 'Testing enhanced error logging system',
          p_error_details: {
            test: true,
            timestamp: new Date().toISOString(),
            component: 'webhook_tester'
          },
          p_severity: 'medium',
          p_session_id: `test_session_${Date.now()}`
        });
        
        if (error) throw error;
        
        // Also test webhook notification
        try {
          const webhookPayload = {
            type: 'error_logging_test',
            error_type: 'test_error',
            message: 'Testing error logging webhook notification',
            timestamp: new Date().toISOString(),
            severity: 'medium'
          };

          const webhookResponse = await fetch(
            'https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTZiMDYzMzA0MzA1MjZiNTUzMjUxMzAi_pc',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(webhookPayload),
              mode: 'no-cors'
            }
          );
        } catch (webhookError) {
          console.log('Webhook test completed (CORS expected)');
        }
        
        return {
          success: true,
          message: 'Error logging test completed successfully',
          error_id: data,
          webhook_sent: true
        };
      }
    },
    {
      name: "Seam Integration Test", 
      description: "Tests the Seam API integration for access code generation",
      type: "manual",
      testFunction: async () => {
        console.log('Testing Seam integration...');
        
        try {
          const { data, error } = await supabase.functions.invoke('seam-integration', {
            body: {
              action: 'test_auth'
            }
          });
          
          if (error) throw error;
          
          return {
            success: true,
            message: 'Seam integration test completed successfully',
            response: data
          };
        } catch (error) {
          console.error('Seam integration test failed:', error);
          throw error;
        }
      }
    }
  ];

  const handleTest = async (webhookFunction: WebhookFunction) => {
    setLoading(webhookFunction.name, true);

    try {
      console.log(`Starting test for: ${webhookFunction.name}`);
      const result = await webhookFunction.testFunction();
      console.log(`Test result for ${webhookFunction.name}:`, result);
      
      toast({
        title: "Test Successful",
        description: `${webhookFunction.name} test completed successfully`,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error(`Test failed for ${webhookFunction.name}:`, error);
      
      toast({
        title: "Test Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(webhookFunction.name, false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Webhook Tester</h2>
        <p className="text-gray-600">
          Test all webhook functions to ensure they're working correctly. 
          This sends actual webhook requests to the configured endpoints.
        </p>
      </div>

      {/* Add the subscription webhook tester */}
      <SubscriptionWebhookTester />

      <div className="border-t pt-8">
        <h3 className="text-xl font-semibold mb-4">Other Webhook Tests</h3>
        <div className="grid gap-4">
          {webhookFunctions.map((webhookFunction) => (
            <Card key={webhookFunction.name}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CardTitle className="text-lg">{webhookFunction.name}</CardTitle>
                    <Badge variant={webhookFunction.type === 'automatic' ? 'default' : 'secondary'}>
                      {webhookFunction.type}
                    </Badge>
                  </div>
                  <Button
                    onClick={() => handleTest(webhookFunction)}
                    disabled={loadingStates[webhookFunction.name]}
                    size="sm"
                  >
                    {loadingStates[webhookFunction.name] ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Test
                  </Button>
                </div>
                <p className="text-sm text-gray-600">{webhookFunction.description}</p>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <strong>Troubleshooting Tips:</strong>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>Check browser console for detailed error messages</li>
                <li>Ensure you have the necessary permissions and data (bookings, users)</li>
                <li>Verify webhook URLs are accessible and responding</li>
                <li>Check the database functions exist and have proper permissions</li>
                <li>Some webhook endpoints may have rate limiting or authentication requirements</li>
                <li>For sessions_per_month testing, ensure you have active subscriptions with this type</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WebhookTester;
